import { useEffect, useState } from "react";
import Toolbar from "../../components/Toolbar";
import AxiosAPI from "../../scripts/AxiosAPI";
import { mkConfig, generateCsv, download } from "export-to-csv";

import { useRef } from "react";
import { SearchOutlined } from "@ant-design/icons";
import type { GetRef, TableColumnsType, TableColumnType } from "antd";
import { Button, Flex, Input, Select, Space, Table } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";

const csvConfig = mkConfig({ useKeysAsHeaders: true });

///// Start Imports

type InputRef = GetRef<typeof Input>;

interface DataType {
  key: string;
  date: string;
  ref: string;
  description: string;
  tenant: string;
  credit: string;
  debit: string;
  balance: string;
}

type DataIndex = keyof DataType;

//// End Imports

function RewardsHistoryComponent() {
  const formatDate: any = (dateString: string) => {
    // Create a new Date object from the date string
    const date = new Date(dateString);

    // Define an array of month names
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    // Extract the day, month, year, and time from the Date object
    const day = date.getDate();
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = date.getMinutes();

    // Format the date string
    const formattedDate = `${day}-${month}-${year}, ${hours}:${
      minutes < 10 ? "0" : ""
    }${minutes}`;

    return formattedDate;
  };

  const [transactionsData, setTransactionData] = useState([]);

  const [totalPages, setTotalPages] = useState(0);

  const [totalElements, setTotalElements] = useState(0);

  ////// Start Table

  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);

  const handleTenantChange:any = (e:any) => {

    console.log("handleTenantChange", e)

  }

  const handleUserChange:any = (e:any) => {

    console.log("handleUserChange", e)

  }

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): TableColumnType<DataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) => (<span>{text}</span>),
  });

  const columns: TableColumnsType<DataType> = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: "15%",
      ...getColumnSearchProps("date"),
    },
    {
      title: "Reference",
      dataIndex: "ref",
      key: "ref",
      width: "12%",
      ...getColumnSearchProps("ref"),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "20%",
      ...getColumnSearchProps("description"),
      sorter: (a, b) => a.description.length - b.description.length,
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Tenant User",
      dataIndex: "tenant",
      key: "tenant",
      ...getColumnSearchProps("tenant"),
      sorter: (a, b) => a.tenant.length - b.tenant.length,
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Debit",
      dataIndex: "debit",
      key: "debit",
      width: "5%",
      ...getColumnSearchProps("debit"),
      sorter: (a, b) => a.debit.length - b.debit.length,
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Credit",
      dataIndex: "credit",
      key: "credit",
      width: "5%",
      ...getColumnSearchProps("credit"),
      sorter: (a, b) => a.credit.length - b.credit.length,
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      width: "5%",
      ...getColumnSearchProps("balance"),
      sorter: (a, b) => a.balance.length - b.balance.length,
      sortDirections: ["descend", "ascend"],
    },
  ];

  ////// End Table

  const [csvData, setCSVData] = useState<any>(undefined);

  const [tenants, setTenants] = useState<any>([]);

  const [users, setUsers] = useState<any>([]);

  const [origialTXNs, setOriginalTXNs] = useState<any>([]);

  const [isTenantDropdown,setIsTenantDropdown] = useState<boolean>(false);

  const populateTenantsAndUsers = (data:any) => {
    const uniqueTenants:Array<any> = [];
    const uniqueUsers:Array<any> = [];
    data.forEach((item:any) => {
      if (!uniqueTenants.some((tenant:any) => tenant.buyerCompanyId === item.buyerCompanyId)) {
        uniqueTenants.push({isTenantDropdown:true, value: item.buyerCompanyName, label:  item.buyerCompanyName, buyerCompanyId: item.buyerCompanyId, buyerCompanyName: item.buyerCompanyName });
      }
      if (!uniqueUsers.some((user:any) => user.firstName === item.buyer.firstName && user.lastName === item.buyer.lastName)) {
        uniqueUsers.push({isTenantDropdown: false, value: item.buyer.firstName+" "+item.buyer.lastName, label: item.buyer.firstName+" "+item.buyer.lastName, firstName: item.buyer.firstName, lastName: item.buyer.lastName, title: item.buyer.title });
      }
    });
    setTenants(uniqueTenants);
    setUsers(uniqueUsers);
  };

  const handleDropdownChange = (dropdownValue:any, selectedValue:any) => {
    const _isTenantDropdown:boolean = selectedValue.isTenantDropdown;
    console.log("selectedValue", selectedValue, _isTenantDropdown, dropdownValue);
    setIsTenantDropdown(_isTenantDropdown);
    if (_isTenantDropdown) {
      // Filter data based on selected tenant
      const filteredData = origialTXNs.filter((item:any) => item.buyerCompanyId === selectedValue);
      // Update your data display based on filteredData
    } else {
      // Filter data based on selected user
      const [selectedFirstName, selectedLastName] = dropdownValue.split(' ');
      const filteredData = origialTXNs.filter((item:any) => item.buyer.firstName === selectedFirstName && item.buyer.lastName === selectedLastName);
      // Update your data display based on filteredData
    }
  };

  useEffect(() => {
    AxiosAPI.getRewardsTransactions()
      .then((res: any) => {
        let _rewardsTransactions: any = res.data.content;
        setOriginalTXNs(_rewardsTransactions);
        populateTenantsAndUsers(_rewardsTransactions);
        const csv = generateCsv(csvConfig)(_rewardsTransactions);

        setCSVData(csv);

        let runningBalance = 0;
        const updatedTransactions = _rewardsTransactions.map(
          (txn: any, txnIndex: number) => {
            runningBalance += txn.credit - txn.debit;
            return {
              key: txnIndex,
              date: formatDate(txn.date),
              ref: "SZ" + txn.reference.toString().padStart(5, "0"),
              description: txn.description,
              tenant: <><strong>{txn.buyerCompanyName}</strong><br/>{txn.buyer.title} {txn.buyer.firstName} {txn.buyer.lastName}</>,
              credit: txn.credit,
              debit: txn.debit,
              balance: runningBalance,
            };
          }
        );

        setTransactionData(updatedTransactions);
      })
      .catch((error: any) => {
        // Handle error
      });
  }, []);

  return (
    <>
      {/*begin::Toolbar*/}
      <div id="kt_app_toolbar" className="app-toolbar  py-3 py-lg-6 ">
        <Toolbar title="Transaction History">
          {/*begin::Toolbar*/}
          <div
            className="d-flex justify-content-end"
            data-kt-user-table-toolbar="base"
            data-select2-id="select2-data-131-i76u"
          >
            {/*begin::Export*/}
            <button
              type="button"
              className="btn btn-light-primary me-3"
              onClick={() => {
                download(csvConfig)(csvData);
              }}
            >
              Export
            </button>
            {/*end::Export*/}
          </div>
          {/*end::Toolbar*/}
        </Toolbar>
      </div>
      {/*end::Toolbar*/}
      {/*begin::Content*/}
      <div
        id="kt_app_content"
        className="app-content  flex-column-fluid "
        data-select2-id="select2-data-kt_app_content"
      >
        {/*begin::Content container*/}
        <div
          id="kt_app_content_container"
          className="app-container  container-xxl "
          data-select2-id="select2-data-kt_app_content_container"
        >
          {/*begin::Card*/}
          <div className="card" data-select2-id="select2-data-134-i7jk">
            {/*begin::Card body*/}
            <div className="card-body py-4">
              {/*begin::Table*/}
              <div
                id="kt_table_users_wrapper"
                className="dt-container dt-bootstrap5 dt-empty-footer"
                style={{ overflowX: "auto" }}
              >
                <Flex gap={32} style={{marginTop: 16, marginBottom: 32}}>
                  <span>Filter by {isTenantDropdown?"Tenant":"Buyer"}</span>
                  <Select 
                    defaultValue="undefined"
                    style={{ width: 240 }}
                    onChange={handleDropdownChange}
                    options={!isTenantDropdown ? tenants : users }
                  />
                  </Flex>

                <Table columns={columns} dataSource={transactionsData} />
              </div>
              {/*end::Table*/}
            </div>
            {/*end::Card body*/}
          </div>
          {/*end::Card*/}
        </div>
        {/*end::Content container*/}
      </div>
      {/*end::Content*/}
    </>
  );
}

export default RewardsHistoryComponent;
